<template>
  <div ref="containerElement" :class="[$style.container, $style[`container${currentWidth}`]]">
    <div :class="[$style.sideBar, {[$style.sideBarFullScreen]: isSidebarFullScreenMode}]">
      <aw-checkout-map-sidebar
        v-if="currentWidth"
        :selected-point="selectedPoint"
        :only-points-scroll="currentWidth !== AVAILABLE_WIDTHS_ENUM.WIDTH1"
        :current-width="currentWidth"
        :available-couriers="availableCouriers"
        :searched-points="searchedPoints"
        :courier-additional-texts="courierAdditionalTexts"
        :courier-promotionals="courierPromotionals"
        :show-mobile-line="currentWidth === AVAILABLE_WIDTHS_ENUM.WIDTH1"
        :heading-tag="headingTag"
        @select-point="setSelectedPoint"
        @search-text-changed="setSearchText"
        @courier-checked-change="handleCourierCheckedChange"
        @mobile-line-click="handleMobileLineClick"
      />
    </div>
    <div :class="$style.map">
      <aw-checkout-osm-map
        v-if="currentWidth && availablePoints.length"
        :is-horizontal="currentWidth !== AVAILABLE_WIDTHS_ENUM.WIDTH3"
        :all-points="availablePoints"
        :all-points-hidden-state="availablePointsHiddenState"
        :selected-point="selectedPoint"
        :disabled-points="disabledPoints"
        @select-point="setSelectedPoint"
        @visible-points="setPointsInsideMapBounds"
      />
      <aw-spinner v-else :class="$style.spinner" />
    </div>
  </div>
</template>

<script setup>

  import { computed, inject, onUnmounted, onMounted, ref, shallowRef, useTemplateRef } from 'vue';
  import { useNuxtApp } from 'nuxt/app';
  import { formatAddress } from '~~/common/utils/addressFormatter';
  import AwCheckoutOsmMap from './AwCheckoutOsmMap';
  import AwCheckoutMapSidebar from '~~/shop/app/components/Page/Checkout/AwCheckoutMapSidebar.vue';
  import AwSpinner from '~~/common/components/Common/AwSpinner.vue';

  const AVAILABLE_WIDTHS_ENUM = {
    WIDTH1: 'width1',
    WIDTH2: 'width2',
    WIDTH3: 'width3',
  };

  const {
    $api,
  } = useNuxtApp();

  const props = defineProps({
    tabData: {
      type: Object,
      required: true,
    },
    headingTag: {
      type: String,
      required: true,
      default: 'h3',
    },
  });

  const currentCheckoutType = inject('currentCheckoutType');
  const emit = defineEmits(['select-pickup-point', 'get-available-points-success']);

  const containerElement = useTemplateRef('containerElement');

  const availableWidths = [
    {
      key: AVAILABLE_WIDTHS_ENUM.WIDTH1,
      minWidth: 0,
      maxWidth: 415,
    },
    {
      key: AVAILABLE_WIDTHS_ENUM.WIDTH2,
      minWidth: 416,
      maxWidth: 535,
    },
    {
      key: AVAILABLE_WIDTHS_ENUM.WIDTH3,
      minWidth: 536,
      maxWidth: Infinity,
    },
  ];
  const currentWidth = ref('');
  const availablePoints = shallowRef([]);
  const availablePointsHiddenState = shallowRef(new Uint8Array(0));
  const pointsInsideMapBounds = shallowRef(new Uint8Array(0));
  const selectedPoint = shallowRef({});
  const searchText = ref('');
  const isSidebarFullScreenMode = ref(false);

  const availableCouriers = computed(() => Object.keys(props.tabData.data));

  const courierAdditionalTexts = computed(() => {
    return Object.entries(props.tabData.data)
      .filter(([, dm]) => dm.nonFoodCourier?.additionalText)
      .map(([key, dm]) => ({
        courier: key,
        text: dm.nonFoodCourier.additionalText,
      }));
  });

  const courierPromotionals = computed(() => {
    return new Set(
      Object.entries(props.tabData.data)
        .filter(([, dm]) => dm.promotional || dm.nonFoodCourier?.promotional)
        .map(([key]) => key),
    );
  });
  const couriers = shallowRef(
    availableCouriers.value.reduce(
      (acc, curr) => {
        acc[curr] = true; // enable all couriers by default
        return acc;
      },
      {},
    ),
  );

  const searchedPoints = computed(() => {
    const aPoints = availablePoints.value;
    const courierFilter = couriers.value;
    const searchFilter = searchText.value.toLowerCase();
    const visiblePoints = [];
    const notVisiblePoints = [];
    const alwaysDisplayedPoints = [];
    for (let idx = 0; idx < aPoints.length; idx++) {
      const point = aPoints[idx];
      if (selectedPoint.value === point) {
        alwaysDisplayedPoints.push(point);
        continue;
      }
      const isVisible = pointsInsideMapBounds.value[idx] === 1;
      if (
        courierFilter[point.type] &&
        (
          !searchFilter ||
          point.name.toLowerCase().includes(searchFilter) ||
          formatAddress(point.address).toLowerCase().includes(searchFilter)
        )
      ) {
        if (isVisible) {
          visiblePoints.push(point);
        } else if (searchFilter) {
          notVisiblePoints.push(point);
        }
      }
    }

    return [
      ...alwaysDisplayedPoints,
      ...visiblePoints,
      ...notVisiblePoints,
    ];
  });

  const disabledPoints = computed(() => {
    const dPoints = new Set();
    const aPoints = availablePoints.value;
    for (let idx = 0; idx < aPoints.length; idx++) {
      if (pointsInsideMapBounds.value[idx] === 1) {
        const point = aPoints[idx];
        if (point && !searchedPoints.value.includes(point)) {
          dPoints.add(point.id);
        }
      }
    }
    return dPoints;
  });

  let resizeObserver = null;
  let idUpdateWidth = null;

  onMounted(() => {
    getAvailablePoints();
    const unwatch = watch(containerElement, (newVal) => {
      if (newVal && !resizeObserver) {
        setTimeout(() => {
          unwatch();
        });
        resizeObserver = new ResizeObserver((entries) => {
          const lastEntry = entries.slice(-1)[0];
          calculateWidth(lastEntry.borderBoxSize.slice(-1)[0].inlineSize);
        });
        resizeObserver.observe(containerElement.value);
        calculateWidth(newVal.clientWidth);
      }
    }, { immediate: true });
  });

  onUnmounted(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  });

  function calculateWidth (width) {
    if (!width) {
      return;
    }
    const newWidthKey = availableWidths.find(
      ({
        minWidth,
        maxWidth,
      }) => width >= minWidth && width <= maxWidth,
    )?.key || '';
    if (!newWidthKey) {
      return;
    }
    clearTimeout(idUpdateWidth);
    idUpdateWidth = setTimeout(() => {
      currentWidth.value = newWidthKey;
    }, 400);
  }

  async function getAvailablePoints () {
    const deliveryMethods = props.tabData.data;
    availablePoints.value = (
      (await Promise.all(
        Object.keys(deliveryMethods).map(async (deliveryMethodId) => {
          const request = $api.$get(`/checkouts/${currentCheckoutType.value}/available_pickup_points/${deliveryMethodId}`);
          const {
            price,
            id: nonFoodCourierId,
          } = deliveryMethods[deliveryMethodId].nonFoodCourier || {};
          const dmPromotional = deliveryMethods[deliveryMethodId].promotional ?? false;
          return ((await request).map((item) => {
            return {
              ...item,
              type: deliveryMethodId,
              price,
              nonFoodCourierId,
              promotional: dmPromotional,
            };
          }));
        }))
      ).flat()
    );
    emit('get-available-points-success');
  }

  function setSelectedPoint (dp) {
    selectedPoint.value = dp;
    if (currentWidth.value === AVAILABLE_WIDTHS_ENUM.WIDTH1) {
      isSidebarFullScreenMode.value = false;
    }
    emit('select-pickup-point', dp);
  }

  function setPointsInsideMapBounds (payload) {
    pointsInsideMapBounds.value = payload;
  }

  let timeoutId = null;

  function setSearchText (searchTextParam) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      searchText.value = searchTextParam;
    }, 400);
  }

  function handleCourierCheckedChange (newCouriers) {
    couriers.value = newCouriers;
  }

  watchEffect(() => {
    const boolArr = new Uint8Array(availablePoints.value.length);
    const avaArr = availablePoints.value;
    const cour = couriers.value;
    for (let idx = 0; idx < boolArr.length; idx++) {
      const point = avaArr[idx];
      boolArr[idx] = cour[point.type] !== false ? 0 : 1;
    }
    availablePointsHiddenState.value = boolArr;
  });

  function handleMobileLineClick () {
    isSidebarFullScreenMode.value = !isSidebarFullScreenMode.value;
  }

  defineExpose({
    setSelectedPoint,
    availablePoints,
  });
</script>


<style module lang="scss" rel="stylesheet/scss">
.container {
  display: grid;
  width: 100%;
  height: 642px;
  margin-top: 20px;
  border: 1px solid $color-border;
  border-radius: 16px;
  grid-template-areas: "map" "sidebar";

  @include tablet(min) {
    margin-top: 24px;
  }

  &width1 {
    .sideBar {
      overflow-y: auto !important;
      width: 100%;
      height: 372px;
      transition: height 0.6s ease, margin-top 0.6s ease;
      @include hide-scrollbar();

      &FullScreen {
        height: 640px;
        margin-top: -268px;
      }
    }

    .map {
      height: 280px;
      margin-bottom: -12px;
    }

  }

  &width2 {
    height: 718px;

    .sideBar {
      width: 100%;
      height: 448px;
    }

    .map {
      height: 280px;
      margin-bottom: -12px;
    }

  }

  &width3 {
    grid-template-areas: "sidebar map";
    grid-template-columns: 256px 1fr;
    height: 538px;

    .sideBar {
      width: 256px;
      height: 536px;
    }

    .map {
      width: calc(100% + 12px);
      height: 536px;
      margin-left: -12px;
    }
  }

  .sideBar {
    z-index: 1;
    overflow: hidden;
    border-radius: 16px;
    background: $color-background-3;
    grid-area: sidebar;
  }

  .map {
    position: relative;
    z-index: 0;
    grid-area: map;

    .spinner {
      position: absolute;
      z-index: 1000;
      top: 0;
      left: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      margin: auto;
      background: rgba($color-seashell, 0.5);
    }
  }
}
</style>
